import { Box } from '@mui/material';
import { CaretDoubleLeft } from '@phosphor-icons/react';
import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSidebarData } from '../../hooks/useSidebarData';
import { AuthService } from '../../service/AuthService';
import { LocalStorageService } from '../../service/LocalStorageService';
import { UserService } from '../../service/UserService';
import { useAuthStore } from '../../store/storeAuth';
import { useStoreGoogleData } from '../../store/storeGoogleData';
import { DashboardManagerProvider } from '../../views/DashboardBeta/Context/DashboardManagerContext';
import ChatBot, { ChatBotHandle } from '../ChatBot/ChatBot';
import SettingsModal from '../Modals';
import MaintenanceAlert from '../UI/MaintenanceAlert';
import { AgentTopBar } from './LayoutBeta/AgentTopBar';
import { AgentsActions } from './LayoutBeta/AgentsActions';
import { AgentsSidebarBody } from './LayoutBeta/AgentsSidebar';
import { CompactSidebar } from './LayoutBeta/CompactSidebar';
import { DataActions } from './LayoutBeta/DataActions';
import { DataSidebarBody } from './LayoutBeta/DataSidebar';
import { ExtendedSidebar } from './LayoutBeta/ExtendedSidebar';
import { TopBar } from './LayoutBeta/TopBar';
import { UserMenu } from './LayoutBeta/UserMenu';
import {
  ITEMS_WITH_EXTENDED_SIDEBAR,
  getNavItems,
} from './LayoutBeta/layoutBetaUtils';
import { FlowModalProvider } from './LayoutBeta/FlowModalContext';
import { migrateVectorMemoryFoldersOnce } from './LayoutBeta/migrateVectorMemoryFoldersOnce';
import { useAccountDisplay } from './LayoutBeta/hooks/useAccountDisplay';

const AgentsSidebarWrapper = () => {
  return <AgentsSidebarBody />;
};

const DataSidebarWrapper = () => {
  return <DataSidebarBody />;
};

const LayoutBeta = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user: authUser,
    setAuthUser,
    setAccessToken,
    folders,
  } = useAuthStore(state => state);
  const { setGoogleAccessToken, setGoogleRefreshToken } = useStoreGoogleData();
  const {
    folders: sidebarFolders,
    spaces: sidebarSpaces,
    isLoading: sidebarLoading,
    refresh: refreshSidebar,
  } = useSidebarData();

  // This is temporarily to migrate the vector memory folders to the new structure
  useEffect(() => {
    if (sidebarLoading) return;
    void migrateVectorMemoryFoldersOnce(
      sidebarFolders,
      sidebarSpaces,
      refreshSidebar,
    );
  }, [sidebarLoading, sidebarFolders, sidebarSpaces, refreshSidebar]);

  const [selectedSidebarItem, setSelectedSidebarItem] = useState<string | null>(
    'home',
  );
  const [extendedSidebarOpen, setExtendedSidebarOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const chatDrawerToggleRef = useRef<ChatBotHandle | null>(null);

  const navItems = getNavItems();
  const { avatarSrc, initials, displayName } = useAccountDisplay(authUser);

  const [previousSidebarItem, setPreviousSidebarItem] = useState<string | null>(
    'home',
  );

  useEffect(() => {
    const handleOpenSettings = () => {
      setPreviousSidebarItem(selectedSidebarItem);
      setSelectedSidebarItem('settings');
      setSettingsModalOpen(true);
    };

    window.addEventListener('open-settings-modal', handleOpenSettings);
    return () => {
      window.removeEventListener('open-settings-modal', handleOpenSettings);
    };
  }, [selectedSidebarItem]);

  useEffect(() => {
    const pathname = location.pathname;
    if (
      pathname.startsWith('/dashboard-beta/agent/') ||
      pathname.startsWith('/dashboard-beta/spaceAgent/')
    ) {
      setSelectedSidebarItem('agents');
      if (ITEMS_WITH_EXTENDED_SIDEBAR.includes('agents' as any)) {
        setExtendedSidebarOpen(true);
      }
    } else if (pathname.startsWith('/dashboard-beta/interface/')) {
      setSelectedSidebarItem('agents');
      if (ITEMS_WITH_EXTENDED_SIDEBAR.includes('agents' as any)) {
        setExtendedSidebarOpen(true);
      }
    } else if (
      pathname === '/dashboard-beta' ||
      (pathname.startsWith('/dashboard-beta/') &&
        !pathname.includes('/agent/') &&
        !pathname.includes('/spaceAgent/') &&
        !pathname.includes('/interface/'))
    ) {
      setSelectedSidebarItem('home');
      setExtendedSidebarOpen(false);
    }
  }, [location.pathname]);

  const handleNavItemClick = (path: string | null, id: string) => {
    if (id === 'settings') {
      setPreviousSidebarItem(selectedSidebarItem);
      setSelectedSidebarItem('settings');
      setSettingsModalOpen(true);
      return;
    }

    setSelectedSidebarItem(id);

    if (id === 'home') {
      if (path) {
        navigate(path);
      }
      setExtendedSidebarOpen(false);
    } else if (ITEMS_WITH_EXTENDED_SIDEBAR.includes(id as any)) {
      setExtendedSidebarOpen(true);
    }
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    setAccessToken(null);
    setAuthUser(null);
    setGoogleAccessToken(null);
    setGoogleRefreshToken(null);
    LocalStorageService.removeItem('accessToken');
    LocalStorageService.removeItem('googleSheetsAccessToken');
    LocalStorageService.removeItem('googleRefreshToken');
    handleUserMenuClose();
    navigate('/login');
  };

  const handleThemeToggle = async () => {
    const newTheme = authUser?.theme === 'dark' ? 'light' : 'dark';
    try {
      await UserService.updateUser({
        theme: newTheme,
      });
      const res = await AuthService.getMe();
      setAuthUser(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const isDarkMode = authUser?.theme === 'dark';
  const isAgentPage =
    location.pathname.startsWith('/dashboard-beta/agent/') ||
    location.pathname.startsWith('/dashboard-beta/spaceAgent/');
  const isInterfacePage = location.pathname.startsWith(
    '/dashboard-beta/interface/',
  );

  const selectedItemLabel =
    navItems.find(item => item.id === selectedSidebarItem)?.label || '';

  const getActionsForItem = () => {
    if (selectedSidebarItem === 'agents') {
      return <AgentsActions />;
    }
    if (selectedSidebarItem === 'data') {
      return <DataActions />;
    }
    return null;
  };

  return (
    <DashboardManagerProvider>
      <FlowModalProvider>
        <div
          className='layout-beta'
          style={{
            backgroundColor: isDarkMode ? '#1D2939' : '#F9FAFB',
            position: 'relative',
            overflow: 'hidden',
            height: '100vh',
            display: 'flex',
          }}
        >
          <CompactSidebar
            navItems={navItems}
            activeItem={selectedSidebarItem}
            userAvatar={avatarSrc}
            userInitials={initials}
            onNavItemClick={handleNavItemClick}
            onUserClick={handleUserMenuClick}
          />

          <UserMenu
            anchorEl={userMenuAnchor}
            isDarkMode={isDarkMode}
            loggedInAsName={displayName}
            loggedInAsAvatarSrc={avatarSrc}
            onClose={handleUserMenuClose}
            onThemeToggle={handleThemeToggle}
            onLogout={handleLogout}
          />

          <SettingsModal
            open={settingsModalOpen}
            onClose={() => {
              setSettingsModalOpen(false);
              setSelectedSidebarItem(previousSidebarItem);
            }}
          />

          {selectedSidebarItem &&
            (ITEMS_WITH_EXTENDED_SIDEBAR as readonly string[]).includes(
              selectedSidebarItem,
            ) && (
              <ExtendedSidebar.Root isOpen={extendedSidebarOpen}>
                <ExtendedSidebar.Header>
                  <ExtendedSidebar.Title>
                    {selectedItemLabel}
                  </ExtendedSidebar.Title>
                  <ExtendedSidebar.Actions>
                    <ExtendedSidebar.ActionButton
                      icon={CaretDoubleLeft}
                      onClick={() => {
                        setExtendedSidebarOpen(false);
                        setSelectedSidebarItem(null);
                      }}
                    />
                    {getActionsForItem()}
                  </ExtendedSidebar.Actions>
                </ExtendedSidebar.Header>
                <ExtendedSidebar.Body>
                  {selectedSidebarItem === 'agents' && <AgentsSidebarWrapper />}
                  {selectedSidebarItem === 'data' && <DataSidebarWrapper />}
                </ExtendedSidebar.Body>
              </ExtendedSidebar.Root>
            )}

          <Box
            sx={{
              flex: 1,
              height: '100vh',
              overflowY: 'hidden',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <MaintenanceAlert />
            {isAgentPage ? (
              <AgentTopBar
                onChatClick={() => {
                  if (chatDrawerToggleRef.current) {
                    chatDrawerToggleRef.current.toggleDrawer();
                  }
                }}
              />
            ) : isInterfacePage ? null : (
              <TopBar
                onAddWidget={() => (window as any).addWidgetToDashboard?.()}
              />
            )}
            <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
              <Outlet />
              {authUser && (
                <ChatBot
                  ref={chatDrawerToggleRef}
                  sideBarOpenWidth={extendedSidebarOpen ? 304 : 47}
                  authUser={authUser}
                  onCreateChat={() => {
                    const chatbotFolder = folders.find(
                      folder => folder.name === 'Chatbot',
                    );
                    if (chatbotFolder) {
                      navigate(
                        `/dashboard-beta/agent/new?folderId=${chatbotFolder.id}&type=chat`,
                      );
                    } else {
                      navigate('/dashboard-beta/agent/new?type=chat');
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </div>
      </FlowModalProvider>
    </DashboardManagerProvider>
  );
};

export default LayoutBeta;
